import {I18n} from '@aws-amplify/core';
import {Badge} from '@mantine/core';

import {PaymentIntentStatus} from '../types';

/**
 * FNP-918: the payment intent `status` as a badge (§13).
 *
 * ═══ MANTINE THEME TOKENS, NOT BOOTSTRAP HEX ═══
 *
 * This previously hardcoded Bootstrap 4 hex values — '#6c757d', '#007bff',
 * '#dc3545' and so on. Two problems, beyond the drift:
 *
 *   1. A fixed hex does not respond to the theme, so the badges kept their
 *      light-mode contrast on a dark background.
 *   2. The old map was semantically WRONG in a way that matters on a money
 *      screen: `requiresAction` was GREEN (#28a745) and `requiresCapture` was
 *      RED (#dc3545). Green reads "done" — but `requiresAction` means the
 *      payment is STALLED waiting on the customer, and `requiresCapture` means
 *      the funds are authorised and healthy, merely uncaptured. The two most
 *      operationally important states were colour-coded backwards.
 *      It also had `succeeded: '#28a748'` — a typo'd near-duplicate of the
 *      green above it, one digit off, so "succeeded" and "requiresAction" were
 *      two indistinguishable greens.
 *
 * The colours below follow the flow: green once the money has actually landed,
 * blue while it is moving on its own, yellow where a human has to do something,
 * grey for a terminal non-event.
 *
 * ⚠️ Colour is NOT the only carrier of meaning — the badge always renders the
 * status text, so it survives a colourblind reader and a greyscale print
 * (§13 Accessibility). Same call as report-runs/profile/StatusBadge.
 */
const STATUS_COLORS: Record<string, string> = {
    // Money has landed.
    [PaymentIntentStatus.succeeded]: 'green',
    // Authorised and healthy — just not captured yet. Not an error.
    [PaymentIntentStatus.requiresCapture]: 'teal',
    // Moving through the rails on its own; nobody needs to act.
    [PaymentIntentStatus.processing]: 'blue',
    // Waiting on a human. These are the rows an operator is looking for.
    [PaymentIntentStatus.requiresAction]: 'yellow',
    [PaymentIntentStatus.requiresConfirmation]: 'yellow',
    [PaymentIntentStatus.requiresPaymentMethod]: 'orange',
    // Terminal, and not a failure to chase.
    [PaymentIntentStatus.canceled]: 'gray',
};

export const StatusBadge = ({status}: {status?: string}) => {

    if (!status) {
        // Nullable field renders the placeholder, never blank (§13).
        // `status` is `PaymentIntentStatus` (nullable) on the schema.
        return <>{'—'}</>;
    }

    return (
        <Badge
            // An unrecognised status renders grey with its own text rather than
            // being forced into one of the seven — a status we do not know about
            // is not an error, and mislabelling it would be worse than showing
            // it verbatim.
            color={STATUS_COLORS[status] ?? 'gray'}
            variant='light'
        >
            {I18n.get(status)}
        </Badge>
    );
};
